import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, fdt, money } from "../../api";
import InfoTip from "../../components/InfoTip";
import { StatusPill } from "../../components/bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inputCls = "cr-input !text-xs !py-2 !rounded-xl";
const btnBlack = "bg-black text-white text-xs font-medium px-3.5 py-2 rounded-lg hover:bg-zinc-800 transition-colors";
const btnAccent = "bg-[#0A0A0A] text-white text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-[#26272A] transition-colors";
const btnOutline = "border border-zinc-300 text-xs font-medium px-3.5 py-2 rounded-lg hover:bg-zinc-50 transition-colors";

export function CmsTab() {
  const [home, setHome] = useState(null);
  const [banners, setBanners] = useState([]);
  const [pages, setPages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [pageForm, setPageForm] = useState(null);
  const [bannerForm, setBannerForm] = useState(null);
  const [faqForm, setFaqForm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/cms/home").then((r) => setHome(r.data.key ? r.data : null)).catch(() => {});
    api.get("/admin/cms/banners").then((r) => setBanners(r.data.banners)).catch(() => {});
    api.get("/admin/cms/pages").then((r) => setPages(r.data.pages)).catch(() => {});
    api.get("/admin/cms/faqs").then((r) => setFaqs(r.data.faqs)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  if (!home) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="ct-cms">
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Homepage hero</h3>
        <div className="grid gap-2">
          <input data-testid="cms-hero-title" value={home.hero_title || ""} onChange={(e) => setHome({ ...home, hero_title: e.target.value })} placeholder="Hero title" className={inputCls} />
          <textarea data-testid="cms-hero-subtitle" rows={2} value={home.hero_subtitle || ""} onChange={(e) => setHome({ ...home, hero_subtitle: e.target.value })} placeholder="Hero subtitle" className={inputCls} />
          <div>
            <p className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Features (JSON)</p>
            <textarea data-testid="cms-features" rows={4} value={JSON.stringify(home.features || [], null, 1)}
              onChange={(e) => { try { setHome({ ...home, features: JSON.parse(e.target.value) }); } catch { /* keep typing */ } }} className={`${inputCls} w-full font-mono`} />
          </div>
          <button data-testid="cms-save-home" onClick={async () => { const { key, ...rest } = home; try { await api.put("/admin/cms/home", rest); toast.success("Homepage saved"); } catch (e) { toast.error(fmtErr(e)); } }} className={`${btnBlack} w-fit`}>Save homepage</button>
        </div>
      </section>

      <section className="cr-card !p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">Banners</h3>
          <button data-testid="add-banner-btn" onClick={() => setBannerForm({ title: "", body: "", cta_label: "", cta_url: "", active: true })} className={btnOutline}>Add banner</button>
        </div>
        {banners.map((b) => (
          <div key={b.id} data-testid={`banner-row-${b.id}`} className="flex items-center justify-between py-2 border-b border-zinc-50 text-xs">
            <span>{b.title} <StatusPill status={b.active ? "active" : "suspended"} /></span>
            <div className="flex gap-2">
              <button data-testid={`banner-toggle-${b.id}`} onClick={async () => { await api.put(`/admin/cms/banners/${b.id}`, { ...b, active: !b.active }); load(); }} className={btnOutline}>{b.active ? "Disable" : "Enable"}</button>
              <button data-testid={`banner-delete-${b.id}`} onClick={async () => { await api.delete(`/admin/cms/banners/${b.id}`); load(); }} className="text-red-500 text-xs">Delete</button>
            </div>
          </div>
        ))}
        {banners.length === 0 && <p className="text-xs text-zinc-400">No banners.</p>}
      </section>

      <section className="cr-card !p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold flex items-center">Pages & SEO<InfoTip text="Published pages are crawlable and appear in the sitemap. Set noindex for utility pages." /></h3>
          <button data-testid="add-page-btn" onClick={() => setPageForm({ slug: "", title: "", body: "", seo_title: "", seo_description: "", published: true, noindex: false })} className={btnOutline}>Add page</button>
        </div>
        {pages.map((p) => (
          <div key={p.id} data-testid={`page-row-${p.id}`} className="flex items-center justify-between py-2 border-b border-zinc-50 text-xs">
            <span>/p/{p.slug} — {p.title} {p.noindex && <span className="text-zinc-400">(noindex)</span>}</span>
            <div className="flex gap-2">
              <button data-testid={`page-edit-${p.id}`} onClick={() => setPageForm(p)} className={btnOutline}>Edit</button>
              <button data-testid={`page-delete-${p.id}`} onClick={async () => { await api.delete(`/admin/cms/pages/${p.id}`); load(); }} className="text-red-500 text-xs">Delete</button>
            </div>
          </div>
        ))}
      </section>

      <section className="cr-card !p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">FAQs</h3>
          <button data-testid="add-faq-btn" onClick={() => setFaqForm({ question: "", answer: "", published: true, order: faqs.length + 1 })} className={btnOutline}>Add FAQ</button>
        </div>
        {faqs.map((f) => (
          <div key={f.id} className="flex items-center justify-between py-2 border-b border-zinc-50 text-xs">
            <span>{f.question}</span>
            <button data-testid={`faq-delete-${f.id}`} onClick={async () => { await api.delete(`/admin/cms/faqs/${f.id}`); load(); }} className="text-red-500 text-xs">Delete</button>
          </div>
        ))}
      </section>

      <Dialog open={!!bannerForm} onOpenChange={() => setBannerForm(null)}>
        <DialogContent data-testid="banner-dialog">
          <DialogHeader><DialogTitle>Banner</DialogTitle></DialogHeader>
          {bannerForm && <div className="space-y-3 mt-2">
            <input data-testid="banner-title" placeholder="Title" value={bannerForm.title} onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="banner-cta-label" placeholder="CTA label" value={bannerForm.cta_label} onChange={(e) => setBannerForm({ ...bannerForm, cta_label: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="banner-cta-url" placeholder="CTA URL (https://…)" value={bannerForm.cta_url} onChange={(e) => setBannerForm({ ...bannerForm, cta_url: e.target.value })} className={`${inputCls} w-full`} />
            <button data-testid="banner-save" onClick={async () => { try { await api.post("/admin/cms/banners", bannerForm); toast.success("Banner saved"); setBannerForm(null); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={`${btnAccent} w-full py-2.5`}>Save</button>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pageForm} onOpenChange={() => setPageForm(null)}>
        <DialogContent data-testid="page-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Page</DialogTitle></DialogHeader>
          {pageForm && <div className="space-y-3 mt-2">
            <input data-testid="page-slug" placeholder="slug (e.g. about)" value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} className={`${inputCls} w-full`} disabled={!!pageForm.id} />
            <input data-testid="page-title" placeholder="Title" value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} className={`${inputCls} w-full`} />
            <textarea data-testid="page-body" rows={6} placeholder="Content" value={pageForm.body} onChange={(e) => setPageForm({ ...pageForm, body: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="page-seo-title" placeholder="SEO title" value={pageForm.seo_title || ""} onChange={(e) => setPageForm({ ...pageForm, seo_title: e.target.value })} className={`${inputCls} w-full`} />
            <input data-testid="page-seo-desc" placeholder="Meta description" value={pageForm.seo_description || ""} onChange={(e) => setPageForm({ ...pageForm, seo_description: e.target.value })} className={`${inputCls} w-full`} />
            <div className="flex gap-4 text-xs">
              <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="page-published" checked={pageForm.published} onChange={(e) => setPageForm({ ...pageForm, published: e.target.checked })} className="accent-[#1C1B1A]" />Published</label>
              <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="page-noindex" checked={pageForm.noindex} onChange={(e) => setPageForm({ ...pageForm, noindex: e.target.checked })} className="accent-[#1C1B1A]" />noindex</label>
            </div>
            <button data-testid="page-save" onClick={async () => {
              try {
                if (pageForm.id) await api.put(`/admin/cms/pages/${pageForm.id}`, pageForm);
                else await api.post("/admin/cms/pages", pageForm);
                toast.success("Page saved"); setPageForm(null); load();
              } catch (e) { toast.error(fmtErr(e)); }
            }} className={`${btnAccent} w-full py-2.5`}>Save page</button>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!faqForm} onOpenChange={() => setFaqForm(null)}>
        <DialogContent data-testid="faq-dialog">
          <DialogHeader><DialogTitle>FAQ</DialogTitle></DialogHeader>
          {faqForm && <div className="space-y-3 mt-2">
            <input data-testid="faq-question" placeholder="Question" value={faqForm.question} onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} className={`${inputCls} w-full`} />
            <textarea data-testid="faq-answer" rows={3} placeholder="Answer" value={faqForm.answer} onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} className={`${inputCls} w-full`} />
            <button data-testid="faq-save" onClick={async () => { try { await api.post("/admin/cms/faqs", faqForm); toast.success("FAQ saved"); setFaqForm(null); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={`${btnAccent} w-full py-2.5`}>Save</button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function EmailsTab() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/email-templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get("/admin/email-logs").then((r) => setLogs(r.data.logs)).catch(() => {});
    api.get("/admin/notification-rules").then((r) => setRules(r.data.rules)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  return (
    <div className="space-y-8" data-testid="ct-emails">
      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-1 flex items-center">Email templates<InfoTip text="Use {{name}}, {{ref}}, {{status}}, {{courier}}, {{amount}}, {{currency}}, {{plan}}, {{doc}}, {{link}}, {{app_url}}. Links must be https. Test sends go to your own admin email." /></h3>
        <div className="divide-y divide-zinc-50 mt-3">
          {templates.map((t) => (
            <div key={t.key} data-testid={`tpl-${t.key}`} className="py-2.5 flex items-center justify-between text-xs">
              <div><p className="font-semibold">{t.key}</p><p className="text-zinc-400">{t.subject}</p></div>
              <div className="flex gap-2">
                <button data-testid={`tpl-test-${t.key}`} onClick={async () => { try { await api.post(`/admin/email-templates/${t.key}/test`); toast.success("Test email sent to your inbox"); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={btnOutline}>Send test</button>
                <button data-testid={`tpl-edit-${t.key}`} onClick={() => setEditing({ ...t })} className={btnBlack}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Notification rules</h3>
        <div className="divide-y divide-zinc-50">
          {rules.map((r) => (
            <div key={r.event} className="py-2.5 flex items-center justify-between text-xs">
              <span className="font-medium capitalize">{r.event.replace(/\./g, " · ").replace(/_/g, " ")}</span>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5"><input type="checkbox" data-testid={`rule-inapp-${r.event}`} checked={r.inapp_enabled} onChange={async (e) => { await api.put(`/admin/notification-rules/${r.event}`, { inapp_enabled: e.target.checked, email_enabled: r.email_enabled }); load(); }} className="accent-[#1C1B1A]" />In-app</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" data-testid={`rule-email-${r.event}`} checked={r.email_enabled} onChange={async (e) => { await api.put(`/admin/notification-rules/${r.event}`, { inapp_enabled: r.inapp_enabled, email_enabled: e.target.checked }); load(); }} className="accent-[#1C1B1A]" />Email</label>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Delivery log</h3>
        <div className="divide-y divide-zinc-50 max-h-64 overflow-y-auto">
          {logs.length === 0 && <p className="text-xs text-zinc-400 py-4">No emails sent yet.</p>}
          {logs.map((l) => (
            <div key={l.id} className="py-2 flex flex-wrap items-center gap-x-3 text-xs">
              <span className="font-mono text-zinc-400">{fdt(l.at)}</span>
              <span className="font-semibold">{l.to}</span>
              <span className="text-zinc-500 truncate max-w-[240px]">{l.subject}</span>
              <StatusPill status={l.status === "sent" ? "delivered" : "rejected"} />
            </div>
          ))}
        </div>
      </section>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="template-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit template: {editing?.key}</DialogTitle></DialogHeader>
          {editing && <div className="space-y-3 mt-2">
            <label className="text-xs block">Subject<input data-testid="tpl-subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className={`${inputCls} w-full mt-1`} /></label>
            <label className="text-xs block">Body (HTML)
              <textarea data-testid="tpl-body" rows={12} value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} className={`${inputCls} w-full mt-1 font-mono`} /></label>
            <button data-testid="tpl-save" onClick={async () => { try { await api.put(`/admin/email-templates/${editing.key}`, { subject: editing.subject, body_html: editing.body_html }); toast.success("Template saved"); setEditing(null); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={`${btnAccent} w-full py-2.5`}>Save template</button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BillingTab() {
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [grants, setGrants] = useState([]);
  const [perkForm, setPerkForm] = useState({ email: "", feature: "radar_ai", days: "" });
  const [form, setForm] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/admin/billing/subscriptions").then((r) => setSubs(r.data.subscriptions)).catch(() => {});
    api.get("/admin/billing/payments").then((r) => setPayments(r.data.payments)).catch(() => {});
    api.get("/admin/billing/grants").then((r) => setGrants(r.data.grants)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const blank = { name: "", role: "courier", price: 9.0, currency: "gbp", interval: "month", features: [], limits: {}, entitlements: {}, trial_days: 0, active: true };

  return (
    <div className="space-y-8" data-testid="ct-billing">
      <section className="cr-card !p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold flex items-center">Plans<InfoTip text="Plans sync to Stripe automatically at checkout. Price/interval/currency are never hardcoded — edit freely." /></h3>
          <button data-testid="add-plan-btn" onClick={() => setForm({ ...blank, featuresText: "" })} className={btnOutline}>New plan</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[560px]">
            <thead><tr className="text-left text-zinc-400 border-b border-zinc-100"><th className="p-2">Name</th><th className="p-2">Role</th><th className="p-2">Price</th><th className="p-2">Trial</th><th className="p-2">Status</th><th className="p-2"></th></tr></thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} data-testid={`plan-row-${p.id}`} className="border-b border-zinc-50">
                  <td className="p-2 font-semibold">{p.name}</td>
                  <td className="p-2 capitalize">{p.role}</td>
                  <td className="p-2">{money(p.price, p.currency?.toUpperCase())}/{p.interval}</td>
                  <td className="p-2">{p.trial_days || 0}d</td>
                  <td className="p-2"><StatusPill status={p.active ? "active" : "suspended"} /></td>
                  <td className="p-2"><button data-testid={`edit-plan-${p.id}`} onClick={() => setForm({ ...p, featuresText: (p.features || []).join("\n") })} className={btnOutline}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Subscriptions</h3>
        {subs.length === 0 && <p className="text-xs text-zinc-400">No subscriptions yet. Assign plans manually from the Users tab or let users subscribe via Stripe checkout.</p>}
        {subs.map((s) => (
          <div key={s.id || s.user_id} data-testid={`sub-${s.user_id}`} className="flex flex-wrap items-center justify-between py-2 border-b border-zinc-50 text-xs">
            <span>{s.user?.email} <span className="text-zinc-400">({s.user?.role})</span></span>
            <span className="font-semibold">{s.plan_name}</span>
            <span className="text-zinc-400">{s.source}</span>
            <StatusPill status={s.status} />
          </div>
        ))}
      </section>

      <section className="cr-card !p-5" data-testid="ct-perks">
        <h3 className="text-sm font-bold mb-1">Individual perks</h3>
        <p className="text-[11px] text-[#A09D95] mb-3">Give one specific user a feature whatever plan they're on — e.g. Radar AI for a beta tester, or auto PDF invoices for a partner.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <input data-testid="perk-email" placeholder="user@email.com" value={perkForm.email} onChange={(e) => setPerkForm({ ...perkForm, email: e.target.value })} className={`${inputCls} flex-1 min-w-[180px]`} />
          <select data-testid="perk-feature" value={perkForm.feature} onChange={(e) => setPerkForm({ ...perkForm, feature: e.target.value })} className={inputCls}>
            <option value="radar_ai">Radar AI copilot</option>
            <option value="invoice_pdf_auto">Auto PDF invoices</option>
          </select>
          <input data-testid="perk-days" type="number" min="1" placeholder="Days (blank = forever)" value={perkForm.days} onChange={(e) => setPerkForm({ ...perkForm, days: e.target.value })} className={`${inputCls} w-40`} />
          <button data-testid="perk-grant-btn" onClick={async () => {
            try {
              await api.post("/admin/billing/grants", { email: perkForm.email, feature: perkForm.feature, days: perkForm.days ? Number(perkForm.days) : null });
              toast.success("Perk granted"); setPerkForm({ ...perkForm, email: "", days: "" }); load();
            } catch (e) { toast.error(fmtErr(e)); }
          }} className={btnOutline}>Grant</button>
        </div>
        {grants.length === 0 && <p className="text-xs text-zinc-400">No individual perks active.</p>}
        {grants.map((g) => (
          <div key={g.email + g.feature} data-testid={`perk-${g.email}-${g.feature}`} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-zinc-50 text-xs">
            <span className="font-semibold">{g.email}</span>
            <span>{g.feature === "radar_ai" ? "Radar AI" : "Auto PDF invoices"}</span>
            <span className="text-zinc-400">{g.until ? `until ${g.until.slice(0, 10)}` : "until revoked"}</span>
            <button data-testid={`perk-revoke-${g.email}-${g.feature}`} onClick={async () => { await api.delete("/admin/billing/grants", { data: { email: g.email, feature: g.feature } }); toast.success("Revoked"); load(); }} className={btnOutline}>Revoke</button>
          </div>
        ))}
      </section>

      <section className="cr-card !p-5">
        <h3 className="text-sm font-bold mb-3">Payments</h3>
        {payments.length === 0 && <p className="text-xs text-zinc-400">No payment events yet.</p>}
        {payments.map((p) => (
          <div key={p.id} data-testid={`payment-${p.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-zinc-50 text-xs">
            <span className="font-mono text-zinc-400">{fdt(p.created_at)}</span>
            <span>{p.plan_name}</span>
            <span className="font-semibold">{p.amount} {p.currency?.toUpperCase()}</span>
            <StatusPill status={p.payment_status} />
            {p.payment_status === "paid" && (
              <button data-testid={`refund-${p.id}`} onClick={async () => { try { await api.post("/admin/billing/refund", { session_id: p.session_id }); toast.success("Refunded"); load(); } catch (e) { toast.error(fmtErr(e)); } }} className={btnOutline}>Refund</button>
            )}
          </div>
        ))}
      </section>

      <Dialog open={!!form} onOpenChange={() => setForm(null)}>
        <DialogContent data-testid="plan-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
          {form && <div className="space-y-3 mt-2">
            <input data-testid="plan-name" placeholder="Plan name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} w-full`} />
            <div className="grid grid-cols-3 gap-2">
              <select data-testid="plan-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                <option value="courier">Courier</option><option value="company">Company</option>
              </select>
              <input data-testid="plan-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className={inputCls} />
              <span data-testid="plan-currency" className={`${inputCls} inline-flex items-center bg-[#F6F3EA] text-[#78756E]`}>GBP £</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <select data-testid="plan-interval" value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} className={inputCls}>
                <option value="month">Monthly</option><option value="year">Yearly</option>
              </select>
              <input data-testid="plan-trial" type="number" placeholder="Trial days" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value) || 0 })} className={inputCls} />
              <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" data-testid="plan-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#1C1B1A]" />Active</label>
            </div>
            <div className="cr-card !p-3 !rounded-xl space-y-1.5" data-testid="plan-entitlements">
              <p className="cr-label !text-[#A09D95]">Included in this plan</p>
              {[["radar_ai", "Radar AI copilot"], ["invoice_pdf_auto", "Auto PDF invoices"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" data-testid={`plan-ent-${k}`} checked={!!(form.entitlements || {})[k]}
                    onChange={(e) => setForm({ ...form, entitlements: { ...(form.entitlements || {}), [k]: e.target.checked } })} className="accent-[#1C1B1A]" />
                  {label}
                </label>
              ))}
            </div>
            <textarea data-testid="plan-features" rows={4} placeholder="Features (one per line)" value={form.featuresText} onChange={(e) => setForm({ ...form, featuresText: e.target.value })} className={`${inputCls} w-full`} />
            <button data-testid="plan-save" onClick={async () => {
              const payload = { name: form.name, role: form.role, price: form.price, currency: form.currency, interval: form.interval, features: form.featuresText.split("\n").map((x) => x.trim()).filter(Boolean), limits: form.limits || {}, entitlements: form.entitlements || {}, trial_days: form.trial_days, active: form.active, discount_pct: form.discount_pct || 0 };
              try {
                if (form.id) await api.put(`/admin/billing/plans/${form.id}`, payload);
                else await api.post("/admin/billing/plans", payload);
                toast.success("Plan saved"); setForm(null); load();
              } catch (e) { toast.error(fmtErr(e)); }
            }} className={`${btnAccent} w-full py-2.5`}>Save plan</button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
